import React, { useEffect } from 'react';
import { X } from 'lucide-react';

function useBodyScrollLock(active) {
  useEffect(() => {
    if (active) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [active]);
}

function SheetHeader({ project, onClose }) {
  return (
    <div className="flex items-center justify-between mb-10">
      <div className="flex items-baseline gap-4">
        <span className="font-display text-orange-acc text-2xl">{project.index}</span>
        <span className="text-[11px] uppercase tracking-[0.3em] text-cream-soft/70">
          Project Detail
        </span>
      </div>
      <button
        onClick={onClose}
        className="w-11 h-11 rounded-full border border-cream/30 flex items-center justify-center text-cream hover:bg-orange-acc hover:text-navy hover:border-orange-acc transition-colors"
        aria-label="close"
      >
        <X size={18} />
      </button>
    </div>
  );
}

function MetaItem({ label, children }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.3em] text-orange-acc">{label}</p>
      <div className="mt-2 text-cream">{children}</div>
    </div>
  );
}

function SheetMeta({ project }) {
  return (
    <div className="mt-10 grid grid-cols-2 gap-6">
      <MetaItem label="Role">
        <ul className="space-y-1">
          {project.role.map((r) => <li key={r}>{r}</li>)}
        </ul>
      </MetaItem>
      <MetaItem label="Tools">
        <ul className="space-y-1">
          {project.tools.map((r) => <li key={r}>{r}</li>)}
        </ul>
      </MetaItem>
      <MetaItem label="Year">{project.year}</MetaItem>
      <MetaItem label="Index">{project.index} / 07</MetaItem>
    </div>
  );
}

function SheetBody({ project }) {
  return (
    <div className="md:col-span-5">
      <h2 className="font-display text-5xl md:text-7xl text-cream leading-[0.95]">
        {project.title}
      </h2>
      <div className="mt-4 flex flex-wrap gap-2">
        {project.tags.map((t) => (
          <span
            key={t}
            className="text-[10px] uppercase tracking-[0.22em] px-2.5 py-1 bg-orange-acc/15 border border-orange-acc/40 text-orange-acc"
          >
            {t}
          </span>
        ))}
      </div>
      <p className="mt-8 text-cream-soft/85 leading-relaxed">{project.description}</p>
      <SheetMeta project={project} />
    </div>
  );
}

function SheetVisual({ project }) {
  return (
    <div className="md:col-span-7">
      <div className="bg-navy-2 border border-cream p-3 md:p-4">
        <img
          src={project.sheet}
          alt={`${project.title} sheet`}
          className="w-full h-auto block"
        />
      </div>
      <p className="mt-3 text-[11px] uppercase tracking-[0.3em] text-cream-soft/60">
        Project sheet — click outside to close
      </p>
    </div>
  );
}

export default function ProjectSheet({ project, onClose }) {
  useBodyScrollLock(Boolean(project));

  if (!project) return null;

  return (
    <div
      className="fixed inset-0 z-[80] bg-navy/95 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="min-h-screen max-w-[1400px] mx-auto px-6 md:px-10 py-10 md:py-16"
        onClick={(e) => e.stopPropagation()}
      >
        <SheetHeader project={project} onClose={onClose} />
        <div className="grid md:grid-cols-12 gap-10 md:gap-14">
          <SheetBody project={project} />
          <SheetVisual project={project} />
        </div>
      </div>
    </div>
  );
}
